/**
 * /setup?token=...&tenant=...
 *
 * Página pública donde el owner invitado canjea su setup-token (que llegó por
 * email) y elige su contraseña. Tras éxito, el backend crea el user con
 * Better-Auth, vincula la membership y devuelve {ok, tenantId}. El usuario
 * puede ir directo a /login a hacer signin (no auto-login para mantener simple
 * el flujo de cookies cross-origin).
 */
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { Button, Input, Label, Card, CardHeader, CardTitle, CardContent } from '@/components/ui'
import { useAuth } from '@/lib/auth-store'
import { Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react'

const searchSchema = z.object({
  token: z.string().optional(),
  tenant: z.string().optional(),
})

export const Route = createFileRoute('/setup')({
  validateSearch: searchSchema,
  component: SetupPage,
})

interface LookupData {
  tenantId: string
  tenantName: string
  ownerEmail: string | null
  expiresAt: string
}

function SetupPage() {
  const search = useSearch({ from: '/setup' })
  const navigate = useNavigate()
  const { apiUrl, setApiUrl } = useAuth()
  const [lookup, setLookup] = useState<LookupData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    if (!search.token) {
      setError('Falta el token de invitación en la URL')
      setLoading(false)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch(`${apiUrl.replace(/\/$/, '')}/api/auth/setup-tenant/lookup?token=${encodeURIComponent(search.token!)}`)
        const json = await res.json().catch(() => null) as { data?: LookupData; error?: { message?: string } } | null
        if (cancelled) return
        if (!res.ok) throw new Error(json?.error?.message ?? 'Token inválido')
        setLookup(json!.data!)
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error al cargar invitación')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [search.token, apiUrl])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden')
      return
    }
    if (password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres')
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      const res = await fetch(`${apiUrl.replace(/\/$/, '')}/api/auth/setup-tenant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: search.token, password, name: name.trim() || undefined }),
      })
      const json = await res.json().catch(() => null) as { error?: { message?: string } } | null
      if (!res.ok) throw new Error(json?.error?.message ?? `HTTP ${res.status}`)
      setSuccess(true)
      setTimeout(() => navigate({ to: '/login' }), 2500)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al configurar cuenta')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Configurar cuenta</CardTitle>
          {lookup && (
            <p className="text-sm text-neutral-400 mt-1">
              Invitación a <strong>{lookup.tenantName}</strong>
              {lookup.ownerEmail && <> · <span className="font-mono">{lookup.ownerEmail}</span></>}
            </p>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 flex items-center justify-center text-neutral-500">
              <Loader2 className="size-5 animate-spin" />
            </div>
          ) : success ? (
            <div className="py-8 text-center space-y-3">
              <CheckCircle2 className="size-12 text-emerald-400 mx-auto" />
              <p className="font-semibold">Cuenta configurada</p>
              <p className="text-sm text-neutral-400">Redirigiendo al login...</p>
            </div>
          ) : !lookup ? (
            <div className="p-4 rounded-md bg-red-900/20 border border-red-800 text-sm text-red-300">
              <div className="flex items-start gap-2">
                <AlertTriangle className="size-4 mt-0.5 shrink-0" />
                <p>{error ?? 'Token inválido o vencido. Pedí al administrador que te genere una nueva invitación.'}</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="apiUrl">API URL</Label>
                <Input id="apiUrl" type="url" value={apiUrl} onChange={(e) => setApiUrl(e.target.value)} required />
              </div>
              <div>
                <Label htmlFor="name">Nombre (opcional)</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Tu nombre" />
              </div>
              <div>
                <Label htmlFor="pwd">Contraseña</Label>
                <Input id="pwd" type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={8} required />
              </div>
              <div>
                <Label htmlFor="pwd2">Repetí contraseña</Label>
                <Input id="pwd2" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} minLength={8} required />
              </div>
              {error && (
                <div className="p-3 rounded-md bg-red-900/20 border border-red-800 text-sm text-red-400">{error}</div>
              )}
              <Button type="submit" disabled={submitting} className="w-full">
                {submitting ? 'Configurando...' : 'Crear cuenta y entrar'}
              </Button>
              <p className="text-xs text-neutral-500 text-center">
                Esta invitación vence el {new Date(lookup.expiresAt).toLocaleDateString('es-CO')}.
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
